/**
 * Prevents "navigation-aborted fetch" noise at the source.
 *
 * When the browser leaves a page (full navigation, reload, tab close) it cancels every in-flight
 * fetch, and each one rejects with `TypeError: Failed to fetch`. Every `catch` in the app then
 * logs it as if it were a real failure — and per-call-site filters (`err.message === 'Failed to
 * fetch'`) are whack-a-mole: one is forgotten and a strict zero-console-errors e2e test flakes.
 * A per-component AbortController does not help either: it only fires on SPA route changes /
 * unmounts, not on a full document unload.
 *
 * Instead: remember that the document is unloading (`pagehide`) and, for fetches that reject
 * afterwards, return a promise that never settles. The page is going away, so nothing can observe
 * the result; no `catch`, logger, or error boundary runs. Fetches that fail while the page is
 * alive are untouched, so genuine network failures still surface.
 *
 * Install once, at app entry, before anything calls `fetch`.
 */
const INSTALLED = Symbol.for('resida.unloadFetchGuard');
export function installUnloadFetchGuard(target = globalThis) {
    const marked = target;
    if (marked[INSTALLED])
        return;
    marked[INSTALLED] = true;
    let unloading = false;
    target.addEventListener('pagehide', () => {
        unloading = true;
    });
    // A page restored from the back/forward cache is alive again.
    target.addEventListener('pageshow', (e) => {
        if (e.persisted)
            unloading = false;
    });
    const originalFetch = target.fetch.bind(target);
    target.fetch = (...args) => originalFetch(...args).catch((err) => {
        if (unloading)
            return new Promise(() => { });
        throw err;
    });
}
//# sourceMappingURL=unload-fetch-guard.js.map